import React, { useCallback, useEffect, useRef, useState } from "react";
import { Upload, Trash2, ImageIcon, FileText, Music, Video, Loader2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import {
  fetchAllMedia,
  uploadMedia,
  deleteMedia,
  resolveMediaUrl,
} from "@/lib/api";
import { MEDIA_KEY_LABELS, useSiteContent } from "@/lib/SiteContent";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

const SLOT_GROUPS = [
  {
    label: "Hero backgrounds",
    keys: ["hero_home", "hero_about", "hero_projects", "hero_services", "hero_contact"],
  },
  {
    label: "Home page",
    keys: ["featured_project"],
  },
  {
    label: "Services page",
    keys: ["service_1", "service_2", "service_3"],
  },
  {
    label: "Projects gallery",
    keys: ["gallery_1", "gallery_2", "gallery_3", "gallery_4"],
  },
  {
    label: "Director photos",
    keys: ["director_1", "director_2", "director_3"],
  },
  {
    label: "3D Map hotspot media",
    keys: [
      "hotspot_entrance_image",
      "hotspot_entrance_audio",
      "hotspot_plots_image",
      "hotspot_plots_audio",
      "hotspot_parks_image",
      "hotspot_parks_audio",
      "hotspot_infrastructure_image",
      "hotspot_infrastructure_audio",
    ],
  },
];

const MediaTypeIcon = ({ type, className }) => {
  if (type === "image") return <ImageIcon className={className} />;
  if (type === "pdf") return <FileText className={className} />;
  if (type === "audio") return <Music className={className} />;
  if (type === "video") return <Video className={className} />;
  return <FileText className={className} />;
};

const formatBytes = (b) => {
  if (!b) return "—";
  const k = 1024;
  const u = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return `${(b / Math.pow(k, i)).toFixed(1)} ${u[i]}`;
};

const SlotCard = ({ slotKey, currentItem, onUpload, onDelete }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const isAudio = slotKey.endsWith("_audio");
  const accept = isAudio ? "audio/*" : "image/*";

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(file, slotKey);
      toast.success("Media uploaded.", { description: MEDIA_KEY_LABELS[slotKey] || slotKey });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Upload failed.", { description: detail || "Please try a different file." });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const url = currentItem?.url ? resolveMediaUrl(currentItem.url) : null;

  return (
    <div className="glass-card neon-border p-4 rounded-[16px] border" data-testid={`media-slot-${slotKey}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] tracking-[0.28em] uppercase text-[color:var(--sp-gold)] truncate">
            {slotKey}
          </p>
          <p className="mt-1 text-sm text-white/85 leading-tight">
            {MEDIA_KEY_LABELS[slotKey] || slotKey}
          </p>
        </div>
      </div>

      <div className="mt-3 relative aspect-[16/10] rounded-[12px] overflow-hidden bg-black/35 border border-white/8">
        {url ? (
          isAudio ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-3">
              <Music className="h-6 w-6 text-[color:var(--sp-gold)]" />
              <audio src={url} controls className="w-full" />
            </div>
          ) : (
            <img src={url} alt={MEDIA_KEY_LABELS[slotKey]} className="absolute inset-0 h-full w-full object-cover" />
          )
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white/35">
            <ImageIcon className="h-6 w-6" />
            <span className="mt-1 text-[10px] tracking-[0.24em] uppercase">Using default</span>
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={onFileChange} data-testid={`media-slot-input-${slotKey}`} />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`media-slot-upload-${slotKey}`}
          className="btn-ghost neon-button inline-flex items-center gap-2 px-3 py-2 text-[10px] tracking-[0.22em] uppercase flex-1 justify-center"
        >
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {uploading ? "Uploading" : currentItem ? "Replace" : "Upload"}
        </button>
        {currentItem && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                type="button"
                data-testid={`media-slot-delete-${slotKey}`}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/15 transition-colors"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#121212] border border-white/12 text-white">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-display text-2xl">Remove this media?</AlertDialogTitle>
                <AlertDialogDescription className="text-white/65">
                  The site will fall back to the default image for this slot. The file will be permanently deleted.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-white/5 border-white/15 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => onDelete(currentItem.id)} className="bg-red-500/90 text-white hover:bg-red-500">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
      {currentItem && (
        <p className="mt-2 text-[10px] text-white/35 truncate">
          {currentItem.originalName || currentItem.filename} · {formatBytes(currentItem.sizeBytes)}
        </p>
      )}
    </div>
  );
};

const BrochureCard = () => null; // Brochure management moved to /admin/brochures

const AdminMediaPage = () => {
  const { mediaByKey, refresh } = useSiteContent();
  const [loading, setLoading] = useState(false);
  const [allMedia, setAllMedia] = useState([]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const m = await fetchAllMedia();
      setAllMedia(m);
    } catch {
      toast.error("Failed to load media library.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleUpload = async (file, key) => {
    await uploadMedia(file, key);
    await Promise.all([loadAll(), refresh()]);
  };
  const handleDelete = async (id) => {
    try {
      await deleteMedia(id);
      toast.success("Media deleted.");
      await Promise.all([loadAll(), refresh()]);
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <AdminLayout
      eyebrow="Media"
      title="Media Manager"
      description="Upload, replace, or remove any image, audio, or PDF used across the website. Changes go live immediately — no code edits required."
      actions={
        <button
          onClick={loadAll}
          data-testid="admin-media-refresh"
          className="btn-ghost inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.22em] uppercase"
        >
          <RefreshCcw className={cn("h-3.5 w-3.5", loading && "animate-spin")} /> Refresh
        </button>
      }
    >
      <BrochureCard />

      <div className="space-y-10">
        {SLOT_GROUPS.map((group) => (
          <section key={group.label}>
            <h2 className="font-display text-2xl md:text-3xl mb-5">{group.label}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {group.keys.map((k) => (
                <SlotCard
                  key={k}
                  slotKey={k}
                  currentItem={mediaByKey?.[k] || null}
                  onUpload={handleUpload}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* Full library list */}
      <section className="mt-12">
        <h2 className="font-display text-2xl md:text-3xl mb-5">All uploaded media</h2>
        {allMedia.length === 0 ? (
          <p className="text-white/50 text-sm">No media uploaded yet.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {allMedia.map((m) => (
              <div key={m.id} className="glass-card p-3 rounded-[14px] border border-white/10">
                <div className="relative aspect-square rounded-md overflow-hidden bg-black/30">
                  {m.type === "image" ? (
                    <img src={resolveMediaUrl(m.url)} alt={m.altText || m.originalName} className="absolute inset-0 h-full w-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-white/55">
                      <MediaTypeIcon type={m.type} className="h-8 w-8" />
                    </div>
                  )}
                </div>
                <div className="mt-2 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    {m.key && (
                      <p className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--sp-gold)] truncate">
                        {m.key}
                      </p>
                    )}
                    <p className="text-[11px] text-white/80 truncate">{m.originalName || m.filename}</p>
                    <p className="text-[10px] text-white/35">{formatBytes(m.sizeBytes)}</p>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="flex h-7 w-7 items-center justify-center rounded-md border border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/15 transition-colors">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[#121212] border border-white/12 text-white">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this media?</AlertDialogTitle>
                        <AlertDialogDescription className="text-white/65">The file will be permanently removed.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="bg-white/5 border-white/15 text-white hover:bg-white/10">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(m.id)} className="bg-red-500/90 text-white hover:bg-red-500">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </AdminLayout>
  );
};

export default AdminMediaPage;
